import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { AppState } from 'react-native';
import { usePathname, useRouter } from 'expo-router';
import { useQueryClient } from '@tanstack/react-query';
import { useNetInfo } from '@react-native-community/netinfo';
import useAuthStore from '@/data/store/useAuthStore';
import useKioskStore from '@/data/store/useKioskStore';
import useGuideStore from '@/data/store/useGuideStore';
import useDropdownStore from '@/data/store/useDropdownStore';
import useConfirmationStore from '@/data/store/useConfirmationStore';
import useMessagesStore from '@/data/store/useMessagesStore';
import useLoader from '@/utils/hooks/useLoader';
import { GuidePresentationContext } from '@/components/Guides/GuideProvider';
import MilestoneCelebrationModal from './MilestoneCelebrationModal';
import { MILESTONES_ENABLED, MILESTONE_PRESENTATION } from '@/config/milestones';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import {
    claimOwnMilestoneCelebrations,
    handleOwnMilestoneCelebrations,
    syncOwnMilestoneEligibility,
} from '@/data/milestones';
import {
    celebrationBatchRoute,
    celebrationBlockedReason,
    isMilestoneLeaseExpired,
    isUsableCelebrationClaim,
    normalizeCelebrationItems,
} from '@/utils/milestones';
import { canViewOrganizationHistory } from '@/utils/milestonePermissions';
import useMilestoneActiveDay from '@/utils/hooks/useMilestoneActiveDay';
import useOrganizationEntitlements from '@/utils/hooks/useOrganizationEntitlements';
import { isFreeOrg } from '@/utils/organizationEntitlements';

/**
 * Reservation id this provider holds on the shared presentation surface
 * (see GuidePresentationContext). Guides win the handshake while starting
 * or active; this reservation blocks new tours for the claim→dismiss window.
 */
const RESERVATION_ID = 'milestone-celebration';

/** Bounded backoff for the idempotent ack — lease expiry recovers beyond this. */
const ACK_RETRY_DELAYS = [800, 2000, 4000];

/** PostgREST "function not found" — backend not deployed yet (staged rollout). */
const isMissingRpcError = (error) =>
    error?.code === 'PGRST202' || error?.originalError?.code === 'PGRST202'
    || error?.status === 404 || error?.originalError?.status === 404;

/**
 * Authenticated app-wide milestone delivery. Server claim/ack leases still
 * deduplicate devices. The shared nonmodal toast survives ordinary navigation;
 * unsafe routes, backgrounding and identity changes release unseen claims.
 */
const MilestoneCelebrationProviderInner = ({ ready = true, blocked: dashboardBlocked = false }) => {
    const router = useRouter();
    const queryClient = useQueryClient();
    const { data: loggedInUser } = Query.people.loggedInUser();
    const accountID = loggedInUser?.accountID ?? null;
    const organizationID = loggedInUser?.organizationID ?? null;
    const personID = loggedInUser?.id ?? null;
    const authenticated = !!(accountID && organizationID && personID);

    const pathname = usePathname();
    const safeRoute = !pathname.includes('/checkin') && !pathname.includes('/give');
    const isEmulating = useAuthStore(s => s.isEmulating);
    const kioskModeEnabled = useKioskStore(s => s.kioskModeEnabled);
    const loaderOpen = useLoader(s => s.isEnabled);
    const confirmationOpen = useConfirmationStore(s => s.isOpen);
    const menuOpen = useDropdownStore(s => s.open);
    // The dashboard boards-feed reaction menu keeps its open state in the
    // messages store — an open menu is not an idle surface.
    const reactionMenuOpen = useMessagesStore(s => !!s.messageForReaction?.contextMenuOpen);
    const registeredBlockers = useGuideStore(s => s.blockers);
    const guide = useContext(GuidePresentationContext);
    const netInfo = useNetInfo();

    const [foreground, setForeground] = useState(AppState.currentState === 'active');
    const [presentation, setPresentation] = useState(null);
    const [rpcUnavailable, setRpcUnavailable] = useState(false);

    const presentationRef = useRef(null);
    const claimingRef = useRef(false);
    // Receipts already acked (or being acked) — a retrying/re-claiming poll
    // must not immediately re-present them.
    const handledReceipts = useRef(new Set());
    // Bumps on identity/org change so in-flight async results from a stale
    // context are ignored (§8.4 generation validation).
    const generation = useRef(0);
    const syncedGeneration = useRef(-1);
    const guideRef = useRef(guide);
    guideRef.current = guide;

    const identityKey = authenticated ? `${accountID}:${organizationID}:${personID}` : null;
    const identityRef = useRef(identityKey);

    /* ------------------------------ signals ------------------------------ */

    useEffect(() => {
        const app = AppState.addEventListener('change', (state) => setForeground(state === 'active'));
        return () => app.remove();
    }, []);

    /* ------------------------- lease lifecycle --------------------------- */

    // RELEASE the held lease best-effort and clear local state. Used on
    // unmount/navigation-away and identity/flag changes — never an ack.
    const releasePresentation = useCallback(() => {
        const current = presentationRef.current;
        presentationRef.current = null;
        setPresentation(null);
        guideRef.current?.release?.(RESERVATION_ID);
        if (current?.leaseID) {
            handleOwnMilestoneCelebrations({ leaseID: current.leaseID, action: 'RELEASE' }).catch(() => {});
        }
    }, []);

    // Identity/org change mid-lease → cancel + RELEASE, clear stale state.
    useEffect(() => {
        if (identityRef.current === identityKey) return;
        identityRef.current = identityKey;
        generation.current += 1;
        handledReceipts.current.clear();
        syncedGeneration.current = -1;
        setRpcUnavailable(false);
        releasePresentation();
    }, [identityKey, releasePresentation]);

    // Ordinary navigation keeps the toast; unsafe contexts release the lease.
    useEffect(() => () => releasePresentation(), [releasePresentation]);
    useEffect(() => {
        if ((!safeRoute || !foreground || isEmulating || kioskModeEnabled) && presentationRef.current) releasePresentation();
    }, [safeRoute, foreground, isEmulating, kioskModeEnabled, releasePresentation]);

    /* --------------------------- eligibility ----------------------------- */

    const foreignBlockers = Object.keys(registeredBlockers).filter(key => key !== RESERVATION_ID).length;
    const blockedReason = celebrationBlockedReason({
        enabled: true,
        authenticated,
        ready,
        focused: safeRoute,
        foreground,
        online: netInfo?.isConnected,
        loaderOpen,
        confirmationOpen,
        menuOpen,
        blockerCount: foreignBlockers,
        guideActive: !!guide?.active,
        presentationReserved: !!guide?.reservedBy && guide.reservedBy !== RESERVATION_ID,
        emulating: isEmulating,
        kiosk: kioskModeEnabled,
        localBlocked: dashboardBlocked || reactionMenuOpen,
    });
    const eligible = blockedReason === null;

    // Own-day tracking runs whenever the provider is mounted with a real
    // identity — the hook itself gates on foreground/day-throttle.
    useMilestoneActiveDay({
        enabled: authenticated && !isEmulating && !kioskModeEnabled,
        accountID,
        organizationID,
        personID,
    });

    // Bounded own eligibility catch-up once per identity generation on a
    // safe foreground transition (§11.3) — creates catch-up receipts without
    // any client-side metric scan.
    useEffect(() => {
        if (!eligible || !authenticated) return;
        const gen = generation.current;
        if (syncedGeneration.current === gen) return;
        syncedGeneration.current = gen;
        (async () => {
            try {
                for (let i = 0; i < 4 && generation.current === gen; i += 1) {
                    const result = await syncOwnMilestoneEligibility();
                    if (result?.more !== true) break;
                }
            } catch { /* adapter already reported unexpected failures */ }
        })();
    }, [eligible, authenticated]);

    /* ------------------------------ polling ------------------------------ */

    const stateQuery = Query.milestones.celebrations({
        organizationID,
        accountID,
        personID,
        options: {
            enabled: eligible && !rpcUnavailable,
            refetchInterval: MILESTONE_PRESENTATION.celebrationPollMs,
            refetchIntervalInBackground: false,
        },
    });
    const celebrationsEnabled = stateQuery.data?.celebrationsEnabled !== false;
    const pendingCount = Number(stateQuery.data?.pendingCount) || 0;
    useEffect(() => {
        if (!celebrationsEnabled && presentationRef.current) releasePresentation();
    }, [celebrationsEnabled, releasePresentation]);

    // A permanently missing RPC (backend not deployed yet) disables polling
    // for this generation instead of spamming ErrorHandler every 30s.
    useEffect(() => {
        if (stateQuery.error && isMissingRpcError(stateQuery.error)) setRpcUnavailable(true);
    }, [stateQuery.error]);

    /* ------------------------------- claim ------------------------------- */

    useEffect(() => {
        if (!eligible || !authenticated || rpcUnavailable) return;
        if (!celebrationsEnabled || !pendingCount) return;
        if (presentationRef.current || claimingRef.current) return;
        // Mutual exclusion with guides: fails when a tour is starting/active
        // or another presenter holds the reservation — next poll retries.
        if (guideRef.current?.reserve && !guideRef.current.reserve(RESERVATION_ID)) return;

        claimingRef.current = true;
        const gen = generation.current;
        let keepReservation = false;

        (async () => {
            try {
                const claim = await claimOwnMilestoneCelebrations({ limit: MILESTONE_PRESENTATION.celebrationClaimLimit });

                // Stale context (auth/org/person changed mid-flight): drop the
                // response and return the lease — never present it.
                if (gen !== generation.current) {
                    if (claim?.leaseID) handleOwnMilestoneCelebrations({ leaseID: claim.leaseID, action: 'RELEASE' }).catch(() => {});
                    return;
                }

                // Empty batch = cross-device lease loss / opt-out mid-flight:
                // a no-op. Expired leases go straight back.
                if (!isUsableCelebrationClaim(claim) || isMilestoneLeaseExpired(claim.leaseExpiresAt)) {
                    if (claim?.leaseID) handleOwnMilestoneCelebrations({ leaseID: claim.leaseID, action: 'RELEASE' }).catch(() => {});
                    return;
                }

                const items = normalizeCelebrationItems(claim.items)
                    .filter(item => !item.receiptID || !handledReceipts.current.has(item.receiptID));
                if (!items.length) {
                    handleOwnMilestoneCelebrations({ leaseID: claim.leaseID, action: 'RELEASE' }).catch(() => {});
                    return;
                }

                const next = { leaseID: claim.leaseID, leaseExpiresAt: claim.leaseExpiresAt, items, identityKey };
                presentationRef.current = next;
                setPresentation(next);
                keepReservation = true;
            } catch {
                // A failing/empty claim must never delay Dashboard, auth, or
                // check-in — the adapter reports unexpected errors already.
            } finally {
                claimingRef.current = false;
                if (!keepReservation) guideRef.current?.release?.(RESERVATION_ID);
            }
        })();
    }, [eligible, authenticated, rpcUnavailable, celebrationsEnabled, pendingCount]);

    /* ------------------------------- ack --------------------------------- */

    // User-acknowledged dismiss (Continue/Escape/back → VIEWED) or
    // programmatic release. The idempotent ack retries briefly, stays scoped
    // to the identity that owned the lease, and is discarded on mismatch —
    // server lease expiry is the recovery path (§8.4).
    const acknowledge = useCallback((action, after) => {
        const current = presentationRef.current;
        presentationRef.current = null;
        setPresentation(null);
        guideRef.current?.release?.(RESERVATION_ID);

        if (current?.leaseID) {
            if (action === 'VIEWED') {
                current.items.forEach(item => { if (item.receiptID) handledReceipts.current.add(item.receiptID); });
            }
            const gen = generation.current;
            const orgID = organizationID;
            const acct = accountID;
            const person = personID;
            const attemptAck = async () => {
                for (let i = 0; i <= ACK_RETRY_DELAYS.length; i += 1) {
                    if (gen !== generation.current) return;
                    try {
                        await handleOwnMilestoneCelebrations({ leaseID: current.leaseID, action });
                        return;
                    } catch (error) {
                        // MILESTONE_LEASE_INVALID etc. are terminal — no retry.
                        if (error?.expected) return;
                    }
                    if (i < ACK_RETRY_DELAYS.length) {
                        await new Promise(resolve => setTimeout(resolve, ACK_RETRY_DELAYS[i]));
                    }
                }
            };
            attemptAck().finally(() => {
                if (gen === generation.current) {
                    queryClient.invalidateQueries({ queryKey: QueryKeys.milestones.celebrations(orgID, acct, person) });
                }
            });
        }
        after?.();
    }, [organizationID, accountID, personID, queryClient]);

    /* ------------------------------ render ------------------------------- */

    const viewAchievementsRoute = presentation
        ? celebrationBatchRoute(presentation.items, { personID, canViewOrgHistory: canViewOrganizationHistory(loggedInUser) })
        : null;

    return (
        <MilestoneCelebrationModal
            visible={!!presentation && presentation.identityKey === identityKey && foreground && safeRoute}
            items={presentation?.items ?? []}
            onDismiss={() => acknowledge('VIEWED')}
            onViewAchievements={viewAchievementsRoute
                ? () => acknowledge('VIEWED', () => router.push(viewAchievementsRoute))
                : null}
        />
    );
};

// Flag gate lives in the wrapper so the hook order inside the provider is
// unconditional — MILESTONES_ENABLED is static for the app's lifetime.
const MilestoneCelebrationProvider = (props) => {
    const { entitlements } = useOrganizationEntitlements();
    return MILESTONES_ENABLED && !isFreeOrg(entitlements) ? <MilestoneCelebrationProviderInner {...props} /> : null;
};

export default MilestoneCelebrationProvider;
